import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { fetchRankedActions } from './utils/fetchRankedActions';

type UseFetchRankedActionsReturnType = {
	rankedActions: string[] | null;
	isLoading: boolean;
	error?: Error;
};

export const useFetchRankedActions = (
	triggerId: string,
	siteId: string,
	featureEnabled: boolean,
	featureView?: string,
): UseFetchRankedActionsReturnType => {
	const { sendAnalyticsEvent } = useAnalytics();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [rankings, setRankings] = useState<string[] | null>(null);
	const timingRef = useRef<{ startTime: number; sent: boolean } | null>(null);

	const makeRankedActionsRequest = useCallback(async () => {
		if (!featureEnabled) {
			setLoading(false);
			setRankings(null);
			return;
		}
		if (!featureView) {
			setError(new Error('No feature view provided'));
			setLoading(false);
			setRankings(null);
			return;
		}

		// Initialize timing tracking
		timingRef.current = { startTime: performance.now(), sent: false };

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'started',
			actionSubject: 'fetchRankedActions',
		});

		try {
			const suggestedRankedActionsResponse = await fetchRankedActions({
				triggerId,
				siteId,
				featureView,
			});

			if (!suggestedRankedActionsResponse) {
				const error = new Error('No ranked actions found');
				if (timingRef.current && !timingRef.current.sent) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'failed',
						actionSubject: 'fetchRankedActions',
						attributes: {
							duration: performance.now() - timingRef.current.startTime,
							errorMessage: error.message,
						},
					});
					timingRef.current.sent = true;
				}
				setError(error);
				setLoading(false);
				setRankings(null);
				return;
			}

			if (
				!suggestedRankedActionsResponse.triggers[0].recommendations[0].recommendation_details
					?.features
			) {
				const error = new Error('No actions property found in response');
				if (timingRef.current && !timingRef.current.sent) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'failed',
						actionSubject: 'fetchRankedActions',
						attributes: {
							duration: performance.now() - timingRef.current.startTime,
							errorMessage: error.message,
						},
					});
					timingRef.current.sent = true;
				}
				setError(error);
				setLoading(false);
				setRankings(null);
				return;
			}
			const rankings =
				suggestedRankedActionsResponse.triggers[0].recommendations[0].recommendation_details
					?.features;
			const rankedActionTypes = rankings.map((action) => action.type);

			// Success tracking
			if (timingRef.current && !timingRef.current.sent) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'succeeded',
					actionSubject: 'fetchRankedActions',
					attributes: {
						duration: performance.now() - timingRef.current.startTime,
						actionsReturned: rankedActionTypes.join(','),
					},
				});
				timingRef.current.sent = true;
			}

			setRankings(rankedActionTypes);
			setLoading(false);
		} catch (err) {
			const error = err instanceof Error ? err : new Error('Unknown error');
			if (timingRef.current && !timingRef.current.sent) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: 'fetchRankedActions',
					attributes: {
						duration: performance.now() - timingRef.current.startTime,
						errorMessage: error.message,
					},
				});
				timingRef.current.sent = true;
			}
			setError(error);
			setLoading(false);
		}
	}, [triggerId, siteId, featureView, featureEnabled, sendAnalyticsEvent]);

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'called',
			actionSubject: 'useFetchRankedActions',
			attributes: {
				isFeatureEnabled: featureEnabled,
				isPageContextTypeSupported: !!featureView,
			},
		});
	}, [featureEnabled, featureView, sendAnalyticsEvent]);

	useEffect(() => {
		makeRankedActionsRequest();
	}, [makeRankedActionsRequest]);

	// Track cancellations when component unmounts during fetch
	useEffect(() => {
		return () => {
			if (timingRef.current && !timingRef.current.sent) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'cancelled',
					actionSubject: 'fetchRankedActions',
					attributes: {
						duration: performance.now() - timingRef.current.startTime,
					},
				});
				timingRef.current.sent = true;
			}
		};
	}, [sendAnalyticsEvent]);

	// Recompute ranking order whenever rankings change
	const rankedActions = useMemo(() => {
		if (rankings && rankings.length === 0) {
			return null;
		}
		return rankings;
	}, [rankings]);

	return useMemo(
		() => ({
			rankedActions,
			isLoading: loading,
			error,
		}),
		[rankedActions, loading, error],
	);
};
